// @ts-nocheck — TODO(auth-cleanup): TS-convert this 540-LOC legacy signup flow.
//   Tracked alongside the contexts/AuthContext shim removal.
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { DebugOverlay } from '../../../debug';

// Helper function to get role-specific messaging
const getRoleMessaging = (roleName) => {
  if (!roleName) return {
    title: 'Externe Fachkraft',
    description: 'Willkommen im FlexWise Portal für externe Partner'
  };

  const role = roleName.toLowerCase();

  // Education professionals
  if (role.includes('teacher') || role.includes('lehrer')) {
    return {
      title: 'Lehrkraft',
      description: 'Willkommen im FlexWise Lehrer-Portal'
    };
  } else if (role.includes('admin') && role.includes('super')) {
    return {
      title: 'Super Administrator',
      description: 'Willkommen im FlexWise Administrator-Portal'
    };
  } else if (role.includes('admin')) {
    return {
      title: 'Administrator',
      description: 'Willkommen im FlexWise Administrator-Portal'
    };
  } else if (role.includes('erzieher') || role.includes('educator')) {
    return {
      title: 'Erzieher/in',
      description: 'Willkommen im FlexWise Pädagogen-Portal'
    };
  } else if (role.includes('parent') || role.includes('eltern')) {
    return {
      title: 'Elternteil',
      description: 'Willkommen im FlexWise Eltern-Portal'
    };
  } else {
    return {
      title: 'Externe Fachkraft',
      description: 'Willkommen im FlexWise Portal für externe Partner'
    };
  }
};

function normalizeError(err) {
  if (!err) return 'Unbekannter Fehler';
  if (typeof err === 'string') return err;
  if (err instanceof Error) {
    try {
      const props = Object.getOwnPropertyNames(err).reduce((a, k)=>{a[k]=err[k]; return a;}, {});
      return err.message || JSON.stringify(props);
    } catch { return String(err); }
  }
  try {
    const json = JSON.stringify(err);
    return json && json !== '{}' ? json : String(err);
  } catch { return String(err); }
}

const DOB_PENDING_STORAGE_KEY = 'fw_pending_dob_child_id';

const persistPendingDobChild = (childId) => {
  if (typeof window === 'undefined') return;
  try {
    if (childId) {
      window.localStorage.setItem(DOB_PENDING_STORAGE_KEY, childId);
    } else {
      window.localStorage.removeItem(DOB_PENDING_STORAGE_KEY);
    }
  } catch (err) {
    console.warn('persistPendingDobChild failed', err);
  }
};

export default function ExternalSignup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [codeData, setCodeData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // Guardian selection for student codes
  const [isStudentCode, setIsStudentCode] = useState(false);
  const [guardianLoaded, setGuardianLoaded] = useState(false);
  const [guardianError, setGuardianError] = useState(null);
  const [guardianOptions, setGuardianOptions] = useState([]);
  const [selectedParentId, setSelectedParentId] = useState('');
  const [guardianAccountNotice, setGuardianAccountNotice] = useState('');
  const [showNewParentForm, setShowNewParentForm] = useState(false);
  const [newParent, setNewParent] = useState({ first_name: '', last_name: '', email: '', relationship: '' });

  // Password policy: min 8, one uppercase, one lowercase, one number
  const passwordValid = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{8,}$/.test(formData.password);
  const passwordsMatch = !!formData.password && !!formData.confirmPassword && formData.password === formData.confirmPassword;

  useEffect(() => {
    const validateCode = async () => {
      const code = searchParams.get('code');
      const nameFromUrl = searchParams.get('name');

      if (!code) {
        navigate('/login?error=missing_code');
        return;
      }

      console.log('External signup - URL parameters:', { code, nameFromUrl });

      try {
        // Activation code verification goes through the verify-user-code edge
        // function (service_role). Direct supabase.rpc('get_user_profile_by_code')
        // was broken by the 2026-05-16 DEFINER→INVOKER sweep — anon has no
        // SELECT on user_codes, so the underlying SECURITY INVOKER RPC throws
        // 42501. The edge function preserves the same { success, error, data }
        // envelope returned by the RPC.
        const { data, error } = await supabase.functions.invoke('verify-user-code', {
          body: { input_code: code }
        });

        if (error) {
          console.error('Code validation error:', error);
          setErrorMessage('Fehler bei der Überprüfung des Einladungscodes. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.');
          return;
        }

        const result = Array.isArray(data) ? data[0] : data;

        if (!result || !result.success) {
          console.error('Profile validation failed:', result);
          setErrorMessage('Der Einladungscode ist ungültig oder bereits abgelaufen. Bitte kontaktieren Sie den Support für einen neuen Code.');
          return;
        }

        // Use name from database result, or fallback to URL parameter
        const databaseName = `${result.data.first_name || ''} ${result.data.last_name || ''}`.trim();
        const finalName = databaseName || nameFromUrl || 'Externe Fachkraft';

        // Determine if this is a student code → requires guardian onboarding
        const lowerRole = String(result.role_name || result.data.role_name || '').toLowerCase();
        const isStudent = lowerRole.includes('student') || lowerRole.includes('schüler');
        setIsStudentCode(!!isStudent);

        // Debug: Log the role data to see what we're getting
        console.log('Role debugging:', {
          role_name: result.role_name,
          result_data: result.data,
          full_result: result
        });

        setCodeData({
          profile_id: result.data.profile_id,
          user_name: finalName,
          school_id: result.data.school_id,
          partner_name: result.cooperation_partner_name || 'External Partner',
          access_code: code,
          first_name: result.data.first_name,
          last_name: result.data.last_name,
          role_name: result.role_name || result.data.role_name
        });

        console.log('Code validation successful:', {
          profile_id: result.data.profile_id,
          user_name: finalName,
          partner_name: result.cooperation_partner_name,
          nameFromUrl,
          databaseName
        });
      } catch (err) {
        console.error('Code validation error:', normalizeError(err));
        setErrorMessage('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.');
      } finally {
        setIsLoading(false);
      }
    };

    validateCode();
  }, [searchParams, navigate]);

  // Load guardian options for student codes
  useEffect(() => {
    const loadGuardians = async () => {
      if (!isStudentCode) return;
      try {
        setGuardianLoaded(false); setGuardianError(null);
        const code = searchParams.get('code');
        const { data, error } = await supabase.rpc('get_student_guardian_options_react', {
          p_code: code,
          p_child_dob: null
        });
        if (error) throw error;
        const res = Array.isArray(data) ? data[0] : data;
        if (!res?.success) {
          setGuardianError(res?.error || 'Konnte Sorgeberechtigte nicht laden');
          setGuardianOptions([]);
        } else {
          setGuardianOptions(res.parents || []);
        }
      } catch (e) {
        setGuardianError(e?.message || 'Unbekannter Fehler beim Laden der Sorgeberechtigten');
        setGuardianOptions([]);
      } finally { setGuardianLoaded(true); }
    };
    loadGuardians();
  }, [isStudentCode, searchParams]);

  const handleSignup = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      alert('Passwörter stimmen nicht überein');
      return;
    }

    try {
      setIsLoading(true);

      let profileIdForAuth = codeData.profile_id;

      if (isStudentCode) {
        // Ensure guardian selection is provided
        if (!selectedParentId && !showNewParentForm) {
          throw new Error('Bitte wählen Sie ein vorhandenes Elternprofil oder legen Sie ein neues an.');
        }
        if (showNewParentForm && (!newParent.first_name || !newParent.last_name)) {
          throw new Error('Bitte Vor- und Nachname für neues Elternprofil angeben.');
        }

        const payloadChoice = !showNewParentForm
          ? { type: 'existing', parent_profile_id: selectedParentId }
          : { type: 'new', first_name: newParent.first_name, last_name: newParent.last_name, email: newParent.email || null, relationship: newParent.relationship || null };

        const { data, error } = await supabase.rpc('create_or_select_parent_for_student_code_react', {
          p_code: searchParams.get('code'),
          p_choice: payloadChoice,
          p_child_dob: null
        });
        if (error) throw error;
        const res = Array.isArray(data) ? data[0] : data;
        if (!res?.success) {
          throw new Error(res?.error || 'Konnte Elternprofil nicht verknüpfen/anlegen');
        }
        if (res?.child_profile_id) {
          persistPendingDobChild(String(res.child_profile_id));
        }
        profileIdForAuth = res.parent_profile_id;
      } else {
        // Non-student: mark code as used against target profile
        const { data: useCodeData, error: useCodeError } = await supabase.rpc('use_code', {
          input_code: searchParams.get('code'),
          profile_id: codeData.profile_id
        });
        if (useCodeError) throw useCodeError;
        const useResult = Array.isArray(useCodeData) ? useCodeData[0] : useCodeData;
        if (!useResult.success) {
          console.error('use_code failed:', useResult);
          const emsg = normalizeError(useResult.error || useResult.message || useResult);
          throw new Error(emsg);
        }
        persistPendingDobChild(null);
      }

      // Create Supabase account with resolved profile_id
      const { error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            profile_id: profileIdForAuth
          }
        }
      });

      if (authError) throw authError;

      // Profile activation is handled by secure RPCs on the server after code validation

      // Navigate to appropriate dashboard based on role
      // Set success state to show verification message
      setSignupSuccess(true);
    } catch (err) {
      console.error('Code validation error:', normalizeError(err));
      setErrorMessage(normalizeError(err) || 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <DebugOverlay name="ExternalSignup.Loading">
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Überprüfe Einladungscode...</p>
          </div>
        </div>
      </DebugOverlay>
    );
  }

  if (errorMessage) {
    return (
      <DebugOverlay name="ExternalSignup.Error">
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-lg text-center">
            <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
              <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">
              Einladungscode Problem
            </h2>
            <p className="mt-4 text-gray-600">
              {errorMessage}
            </p>
            <div className="mt-6 space-y-3">
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 transition-colors"
              >
                Zur Anmeldung
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              Benötigen Sie Hilfe? Kontaktieren Sie den Support oder Ihre Schule.
            </p>
          </div>
        </div>
      </DebugOverlay>
    );
  }

  if (signupSuccess) {
    return (
      <DebugOverlay name="ExternalSignup.Success">
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-lg text-center">
            <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">
              E-Mail bestätigen
            </h2>
            <p className="mt-4 text-gray-600">
              Vielen Dank für Ihre Registrierung! Wir haben eine Bestätigungs-E-Mail an <strong>{formData.email}</strong> gesendet.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mt-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Nächste Schritte:
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Überprüfen Sie Ihren E-Mail-Eingang</li>
                      <li>Klicken Sie auf den Bestätigungslink in der E-Mail</li>
                      <li>Melden Sie sich mit Ihren Zugangsdaten an</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 space-y-3">
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 transition-colors"
              >
                Zur Anmeldung
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              Keine E-Mail erhalten? Überprüfen Sie Ihren Spam-Ordner oder kontaktieren Sie den Support.
            </p>
          </div>
        </div>
      </DebugOverlay>
    );
  }

  return (
    <DebugOverlay name="ExternalSignup">
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-lg">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">
              Willkommen bei FlexWise
            </h2>
            <p className="mt-2 text-gray-600">
              {(() => {
                const role = (codeData?.role_name || '').toLowerCase();
                if (role.includes('student') || role.includes('schüler')) {
                  return (
                    <>
                      <span className="font-semibold text-blue-600">Sorgeberechtigte</span> – für {codeData?.user_name}
                    </>
                  );
                }
                const roleMessaging = getRoleMessaging(codeData?.role_name);
                return (
                  <>
                    <span className="font-semibold text-blue-600">{roleMessaging.title}</span> - {roleMessaging.description}
                  </>
                );
              })()}
            </p>
            {isStudentCode && selectedParentId && (
              <div className="mt-4 mb-4 bg-slate-50 border border-slate-200 rounded-md p-3 text-left">
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    Ausgewählt: <span className="font-medium">{guardianOptions.find(p => p.id === selectedParentId)?.name || 'Elternprofil'}</span>
                  </div>
                  <button
                    type="button"
                    className="text-sm text-blue-600 hover:underline"
                    onClick={() => {
                      setSelectedParentId('');
                      setGuardianAccountNotice('');
                    }}
                  >Zurück ändern</button>
                </div>
              </div>
            )}
            {isStudentCode && showNewParentForm && (
              <div className="mt-4 mb-4 bg-slate-50 border border-slate-200 rounded-md p-3 text-left">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-slate-800">Neues Elternprofil</div>
                  <button
                    type="button"
                    className="text-sm text-blue-600 hover:underline"
                    onClick={() => {
                      setShowNewParentForm(false);
                      setGuardianAccountNotice('');
                    }}
                  >Zurück</button>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  <input className="border rounded px-2 py-2 text-sm" placeholder="Vorname" value={newParent.first_name} onChange={(e)=>setNewParent({...newParent, first_name:e.target.value})} />
                  <input className="border rounded px-2 py-2 text-sm" placeholder="Nachname" value={newParent.last_name} onChange={(e)=>setNewParent({...newParent, last_name:e.target.value})} />
                  <input className="border rounded px-2 py-2 text-sm" placeholder="E-Mail (optional)" type="email" value={newParent.email} onChange={(e)=>setNewParent({...newParent, email:e.target.value})} />
                  <input className="border rounded px-2 py-2 text-sm" placeholder="Verhältnis (optional, z.B. Mutter/Vater)" value={newParent.relationship} onChange={(e)=>setNewParent({...newParent, relationship:e.target.value})} />
                </div>
              </div>
            )}
            {isStudentCode && !selectedParentId && !showNewParentForm && (
              <div className="mt-4 mb-4 bg-slate-50 border border-slate-200 rounded-md p-3 text-left">
                <div className="text-sm font-medium text-yellow-800 mb-2">Wer sind Sie?</div>
                {!guardianLoaded && <div className="text-sm text-yellow-800">Lade Optionen…</div>}
                {guardianError && <div className="text-sm text-red-700">{guardianError}</div>}
                {guardianLoaded && !guardianError && (
                  <div className="space-y-3">
                    {guardianOptions.length > 0 && (
                      <div className="grid gap-2">
                        {guardianOptions.map((p)=> {
                          const maybeStatus = typeof p?.account_status === 'string' ? p.account_status.toLowerCase() : null;
                          const parentHasAccount = Boolean(
                            p?.has_account ??
                            p?.hasAccount ??
                            (maybeStatus ? ['active', 'invited', 'created'].includes(maybeStatus) : false)
                          );
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => {
                                if (parentHasAccount) {
                                  const name = p?.name || 'Dieses Elternprofil';
                                  setGuardianAccountNotice(`${name} hat bereits ein Konto. Bitte melden Sie sich an und fügen Sie das Kind über „Kind hinzufügen“ hinzu.`);
                                  return;
                                }
                                setGuardianAccountNotice('');
                                setSelectedParentId(p.id);
                                setShowNewParentForm(false);
                              }}
                              className={`w-full text-left border rounded px-3 py-2 text-sm hover:border-blue-400 ${selectedParentId===p.id ? 'border-blue-600 ring-2 ring-blue-200' : 'border-slate-200'}`}
                            >
                              <div className="font-medium">{p.name || 'Elternprofil'}</div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="my-2 text-center text-xs text-slate-500">oder</div>
                    {!showNewParentForm ? (
                      <button
                        type="button"
                        className="w-full text-left border rounded px-3 py-2 text-sm hover:border-blue-400"
                        onClick={()=>{
                          setShowNewParentForm(true);
                          setSelectedParentId('');
                          setGuardianAccountNotice('');
                        }}
                      >
                        Neues Elternprofil anlegen
                      </button>
                    ) : (
                      <div className="grid grid-cols-1 gap-2">
                        <input className="border rounded px-2 py-2 text-sm" placeholder="Vorname" value={newParent.first_name} onChange={(e)=>setNewParent({...newParent, first_name:e.target.value})} />
                        <input className="border rounded px-2 py-2 text-sm" placeholder="Nachname" value={newParent.last_name} onChange={(e)=>setNewParent({...newParent, last_name:e.target.value})} />
                        <input className="border rounded px-2 py-2 text-sm" placeholder="E-Mail (optional)" type="email" value={newParent.email} onChange={(e)=>setNewParent({...newParent, email:e.target.value})} />
                        <input className="border rounded px-2 py-2 text-sm" placeholder="Verhältnis (optional, z.B. Mutter/Vater)" value={newParent.relationship} onChange={(e)=>setNewParent({...newParent, relationship:e.target.value})} />
                      </div>
                    )}
                  </div>
                )}
                {guardianAccountNotice && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                    {guardianAccountNotice}
                  </div>
                )}
              </div>
            )}

            <div className="mt-3 bg-green-50 border border-green-200 rounded-md p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-4 w-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-2">
                  <p className="text-sm text-green-800">
                    <strong>Einladungscode gültig:</strong> {codeData?.access_code}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {((!isStudentCode) || selectedParentId || (showNewParentForm && newParent.first_name && newParent.last_name)) && (
          <form className="mt-8 space-y-6" onSubmit={handleSignup}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  E-Mail-Adresse für Ihr Konto *
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="ihre.email@example.com"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Diese E-Mail wird für die Anmeldung und Kommunikation verwendet
                </p>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Passwort wählen *
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Mindestens 8 Zeichen"
                  minLength={8}
                  pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{8,}"
                  onInvalid={(e) => e.currentTarget.setCustomValidity('Bitte das geforderte Format verwenden: mindestens 8 Zeichen, einen Großbuchstaben, einen Kleinbuchstaben und eine Zahl.')}
                  onInput={(e) => e.currentTarget.setCustomValidity('')}
                />
                <p className="mt-2 text-sm text-gray-700">
                  <span className="inline-block mr-1">📦</span>
                  Das Passwort braucht: mindestens 8 Zeichen, einen Großbuchstaben, einen Kleinbuchstaben und eine Zahl.
                </p>
                {!passwordValid && formData.password && (
                  <p className="mt-1 text-sm text-red-600">Passwort erfüllt die Anforderungen nicht.</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Passwort bestätigen *
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Passwort wiederholen"
                  onInput={(e) => {
                    e.currentTarget.setCustomValidity('');
                    if (e.currentTarget.value && e.currentTarget.value !== formData.password) {
                      e.currentTarget.setCustomValidity('Passwörter stimmen nicht überein.');
                    }
                  }}
                />
                {formData.confirmPassword && !passwordsMatch && (
                  <p className="mt-1 text-sm text-red-600">Passwörter stimmen nicht überein.</p>
                )}
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Datenschutz und Sicherheit
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      Mit der Registrierung stimmen Sie der Verarbeitung Ihrer Daten gemäß der DSGVO zu.
                      Ihre Daten werden nur für die Zusammenarbeit mit der Schule verwendet.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={
                isLoading ||
                !formData.email || !passwordValid || !formData.confirmPassword || !passwordsMatch ||
                (isStudentCode && (
                  (!selectedParentId && !showNewParentForm) ||
                  (showNewParentForm && (!newParent.first_name || !newParent.last_name)) ||
                  !guardianLoaded || !!guardianError
                ))
              }
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                  Konto wird erstellt...
                </div>
              ) : (
                (() => {
                  const role = (codeData?.role_name || '').toLowerCase();
                  if (role.includes('student') || role.includes('schüler')) {
                    return 'Elternteil Konto erstellen';
                  }
                  const roleMessaging = getRoleMessaging(codeData?.role_name);
                  return `${roleMessaging.title} Konto erstellen`;
                })()
              )}
            </button>
          </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Sie haben bereits ein Konto? <a href="/login" className="text-blue-600 hover:text-blue-500">Hier anmelden</a>
            </p>
          </div>
        </div>
      </div>
    </DebugOverlay>
  );
}
